import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import ToggleGroup from 'components/controls/ToggleGroup';
import TimeRangeSelector from './TimeRangeSelector';
import RqHealthWidget from './RqHealthWidget';
import BackupStatusWidget from './BackupStatusWidget';
import DatabaseSizeWidget from './DatabaseSizeWidget';
import TotalMrrWidget from './TotalMrrWidget';
import LatestOrdersCard from './LatestOrdersCard';
import LatestTrialsCard from './LatestTrialsCard';
import GrowthSection from './GrowthSection';
import DynoMonitoringSection from './MonitoringSection';
import { AdminRefreshContext } from './AdminRefreshContext';
import { Deployment, DynoType } from './monitoringTypes';
import { TimeRange } from './useDynoMetrics';
import '../../../style/scss/dashboard.scss';

const DEFAULT_RANGE_HOURS = 3;

// Dyno types selectable in the monitoring section
const DYNO_TYPES: DynoType[] = ['web', 'worker', 'syncworker', 'scheduler'];

interface AdminDashboardContentProps {
    tvMode?: boolean;
    viewerToken?: string;
}

// Renders the full admin dashboard layout, shared by the normal
// page and the tv view which hides the interactive controls
const AdminDashboardContent: React.FC<AdminDashboardContentProps> = ({ tvMode, viewerToken }) => {
    const { t } = useTranslation();
    const [fromDate, setFromDate] = useState<Date>(new Date(Date.now() - DEFAULT_RANGE_HOURS * 60 * 60 * 1000));
    const [toDate, setToDate] = useState<Date>(new Date());
    const [dynoType, setDynoType] = useState<DynoType>('web');
    const [deployment, setDeployment] = useState<Deployment>('production');
    const [refreshNonce, setRefreshNonce] = useState<number>(0);

    const range: TimeRange = { from: fromDate, to: toDate };

    // The tv view keeps showing the deployment of its own server,
    // so it sends no deployment param at all
    const selectedDeployment = tvMode ? undefined : deployment;

    // Build the dyno selector options from the dyno types
    const dynoOptions = DYNO_TYPES.map(type => ({
        value: type,
        label: t(`dashboard.monitoring.dyno_type.${type}`),
    }));

    return (
        <AdminRefreshContext.Provider value={{ refreshNonce, pollingEnabled: Boolean(tvMode) }}>
        <div className={`dashboard admin-dashboard${tvMode ? ' admin-dashboard-tv-content' : ''}`}>
            <div className='greeting'>
                <h2>Crafter Admin Dashboard</h2>
            </div>

            {/* Status row with the job health, backup and database signals */}
            <div className='admin-status-row'>
                <RqHealthWidget viewerToken={viewerToken} />
                <BackupStatusWidget viewerToken={viewerToken} />
                <DatabaseSizeWidget viewerToken={viewerToken} deployment={selectedDeployment} />
            </div>

            {/* Server monitoring header with the deployment, dyno and range controls */}
            <div className='admin-section-header'>
                <h5>{t('dashboard.monitoring.title')}</h5>
                {!tvMode && (
                    <div className='admin-section-controls'>
                        <ToggleGroup<Deployment>
                            value={deployment}
                            onChange={setDeployment}
                            options={[
                                { value: 'staging', label: t('dashboard.monitoring.deployment.staging') },
                                { value: 'production', label: t('dashboard.monitoring.deployment.production') },
                            ]}
                        />
                        <ToggleGroup value={dynoType} onChange={setDynoType} options={dynoOptions} />
                        <TimeRangeSelector
                            fromDate={fromDate}
                            toDate={toDate}
                            refreshNonce={refreshNonce}
                            onChange={(newFrom, newTo) => {
                                setFromDate(newFrom);
                                setToDate(newTo);
                            }}
                        />
                        {/* Refetches every widget and slides the preset window to now */}
                        <button
                            type='button'
                            className='admin-refresh-button'
                            title={t('dashboard.monitoring.refresh_label')}
                            aria-label={t('dashboard.monitoring.refresh_label')}
                            onClick={() => setRefreshNonce(nonce => nonce + 1)}
                        >
                            <FontAwesomeIcon icon={faArrowsRotate} />
                        </button>
                    </div>
                )}
            </div>

            {/* Charts of the selected dyno type and deployment */}
            <DynoMonitoringSection dynoType={dynoType} range={range} viewerToken={viewerToken} deployment={selectedDeployment} />

            {/* New trials, new orders and the total mrr side by side */}
            <div className='admin-cards-grid-3'>
                <LatestTrialsCard viewerToken={viewerToken} />
                <LatestOrdersCard viewerToken={viewerToken} />
                <div className='admin-side-column'>
                    <TotalMrrWidget viewerToken={viewerToken} />
                </div>
            </div>

            {/* Growth chart over the full width */}
            <GrowthSection viewerToken={viewerToken} />
        </div>
        </AdminRefreshContext.Provider>
    );
};

export default AdminDashboardContent;
